import _get from 'lodash/get'
import React, { useMemo, useState } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { Pressable, Text, TextInput, View } from 'react-native'
import { tv } from 'tailwind-variants'
import { P, match } from 'ts-pattern'

import { mapLegacyIconToPhosphor } from '@nxt-insurance/constants'

import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import { EVENTS } from '@flows/configs/analytics/constants'
import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'
import type { ProductExtension, ProductKey } from '@flows/index'
import { getProductParamPath } from '@flows/insurance/utils'
import { IMAGE } from '@ui-kit/assets/images/index.web'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import Icon from '@ui-kit/components/SvgIcon/index.web'
import Switch from '@ui-kit/components/formComponents/Switch/index.web'
import Spacer from '@ui-kit/components/layoutComponents/Spacer'
import { body1, body2, h3, h4 } from '@ui-kit/core/typography'

import { QuoteModal } from './QuoteModal'
import { SubGroupAccordion } from './SubGroupAccordion'
import { usePriceChangeTracking } from './hooks'

const extensionStyles = tv({
  slots: {
    container: 'gap-y-4 pt-12',
    card: 'rounded-xl border border-grey-200 p-4',
    toggleRow: 'flex-row items-center justify-between',
    toggleContent: 'flex-1 flex-row items-center gap-x-4',
    // Mirrors @ui-kit SwitchCard's icon container so extensions look identical
    // to the rest of the app (and to website-react's `<UiKit.SwitchCard>`).
    iconWrapper: 'h-12 w-12 items-center justify-center rounded-full bg-deep-forest/5',
    textWrapper: 'flex-1 gap-y-0.5',
    modalHeader: 'mb-3 flex-row items-start justify-between',
    modalTitleText: 'flex-1',
    modalCloseButton: 'pl-3',
    modalInput: 'rounded-lg border border-grey-300 px-4 py-3 text-base',
    modalInfoBox: 'rounded-lg border border-grey-200 bg-grey-100 p-3',
    modalConfirmButton: 'w-full items-center rounded-lg py-3',
  },
  variants: {
    confirmDisabled: {
      true: { modalConfirmButton: 'bg-grey-300' },
      false: { modalConfirmButton: 'bg-black' },
    },
  },
})

export type ExtensionExtraContent = {
  content: AnyStepContentElement[]
  requiredPaths?: string[]
}

type Props = {
  extensions: ProductExtension[]
  productKey: ProductKey
  extraContent?: Record<string, ExtensionExtraContent>
  stepContentRenderer: (props: any) => React.ReactElement
  componentMap?: Record<string, React.ElementType>
}

const EXTENSION_EXTRA_PARAMS: Record<
  string,
  {
    paramName: string
    label: string
    min: number
    max: number
    infoText: string
  }
> = {
  bike_theft_coverage: {
    paramName: 'bike_theft_insured_sum_amount',
    label: 'Purchase price',
    min: 500,
    max: 5000,
    infoText:
      'You can also cover several bikes. In this case you have to declare the purchase price of all bikes together. Please note that the insured sum must be between EUR 500 and EUR 5,000.',
  },
}

const getExtraParamsForExtension = (extension: ProductExtension) => {
  const itemKey = extension.configurationDataExtensionKey
  const direct = EXTENSION_EXTRA_PARAMS[itemKey]
  if (direct) {
    return direct
  }

  const lowerKey = itemKey.toLowerCase()
  const lowerTitle = (extension.title ?? '').toLowerCase()
  const lowerDescription = (extension.description ?? '').toLowerCase()
  const looksLikeBikeTheft =
    lowerKey.includes('bike') ||
    lowerKey.includes('theft') ||
    lowerKey.includes('fahrrad') ||
    lowerTitle.includes('bike') ||
    lowerTitle.includes('fahrrad') ||
    lowerDescription.includes('bike') ||
    lowerDescription.includes('fahrrad')

  return looksLikeBikeTheft ? EXTENSION_EXTRA_PARAMS.bike_theft_coverage : undefined
}

type ItemProps = {
  extension: ProductExtension
  productKey: ProductKey
  premiumIntervalKey: 'p1M' | 'p1Y'
  premiumIntervalLabel: string
  hasModal?: boolean
  onOpenExtraParamModal: (extension: ProductExtension) => void
  onOpenLearnMore: (extension: ProductExtension) => void
}

// The personalization service returns the icon as a name (e.g. `bike_24`). We map
// it to a Phosphor icon first (matching the react-native app and flows-ui `Icon`),
// then fall back to the legacy `@ui-kit` SVG asset or a CDN `url`.
const SMALL_ICONS: { [name: string]: unknown } = IMAGE.icons.small

const getExtensionIconName = (extension: ProductExtension) =>
  extension.icon?.small?.name ?? extension.icon?.medium?.name ?? extension.icon?.large?.name

const renderExtensionIcon = (extension: ProductExtension) => {
  const name = getExtensionIconName(extension)
  const phosphorName = name ? mapLegacyIconToPhosphor(name) : undefined
  if (phosphorName) {
    return <PhosphorIcon name={phosphorName} size={24} />
  }

  const asset = name && SMALL_ICONS[name] ? SMALL_ICONS[name] : undefined
  const src =
    asset ?? extension.icon?.small?.url ?? extension.icon?.medium?.url ?? extension.icon?.large?.url
  return src ? <Icon imgSrc={src} /> : null
}

const styles = extensionStyles()

const ExtensionItemSimple = (itemProps: ItemProps) => {
  const { control } = useFormContext()
  const { t } = useTranslation()
  const { formatCurrency, trackEvent } = useQuoteData()
  const { trackPriceChange } = usePriceChangeTracking()

  const itemKey = itemProps.extension.configurationDataExtensionKey
  const name = getProductParamPath(itemProps.productKey, itemKey)
  const switchId = `products-${itemProps.productKey}-configuration_parameters-${itemKey}-Switch`
  const { field } = useController({ control, name })
  const isSelected = Boolean(field.value)

  const onToggle = (value: boolean) => {
    field.onChange(value)
    trackEvent('ExtensionSwitchToggled', { id: name, value })
    trackPriceChange('extension_toggle')
  }

  const onTogglePress = () => onToggle(!isSelected)
  const minPrice = itemProps.extension.minPremium[itemProps.premiumIntervalKey]

  return (
    <View className={styles.card()}>
      <Pressable className={styles.toggleRow()} onPress={onTogglePress}>
        <View className={styles.toggleContent()}>
          {renderExtensionIcon(itemProps.extension) ? (
            <View className={styles.iconWrapper()}>{renderExtensionIcon(itemProps.extension)}</View>
          ) : null}
          <View className={styles.textWrapper()}>
            <Text className={h4()}>{itemProps.extension.title}</Text>
            {!isSelected && (
              <Text className={body2({ color: 'grey500' })}>
                from +{formatCurrency(minPrice)}/{itemProps.premiumIntervalLabel}
              </Text>
            )}
          </View>
        </View>
        <Switch
          id={switchId}
          testID={switchId}
          value={isSelected}
          onValueChange={onToggle}
          label={itemProps.extension.title}
        />
      </Pressable>
      <Spacer size="xsmall" />
      <Text className={body2({ color: 'grey700' })}>{itemProps.extension.description}</Text>
      <Spacer size="xsmall" />
      <Pressable onPress={() => itemProps.onOpenLearnMore(itemProps.extension)}>
        <Text className={body2({ color: 'deepForest' })}>
          {t('sharedFlows_common:steps.quote.extensions.learn_more')} {'>'}
        </Text>
      </Pressable>
    </View>
  )
}

const ExtensionItemWithExtraModal = (itemProps: ItemProps) => {
  const { control, watch } = useFormContext()
  const { t } = useTranslation()
  const { formatCurrency, trackEvent } = useQuoteData()
  const { trackPriceChange } = usePriceChangeTracking()

  const itemKey = itemProps.extension.configurationDataExtensionKey
  const name = getProductParamPath(itemProps.productKey, itemKey)
  const switchId = `products-${itemProps.productKey}-configuration_parameters-${itemKey}-Switch`
  const { field } = useController({ control, name })
  const isSelected = Boolean(field.value)
  const values = watch()

  const isApplicable = useMemo(() => {
    if (!(itemKey.includes('natural_disasters') || itemKey.includes('naturalDisasters'))) {
      return true
    }
    const productConfig = _get(
      values,
      ['products', itemProps.productKey, 'configuration_parameters'],
      {}
    ) as Record<string, unknown>

    const restrictions = itemProps.extension.configurationParamRestrictions ?? []
    if (!restrictions.length) {
      return productConfig?.natural_disasters_coverage_eligible !== false
    }
    return restrictions.every((restriction) => {
      const localValue = productConfig?.[restriction.parameterName]
      const rootValue = _get(values, restriction.parameterName)
      const currentValue = localValue ?? rootValue
      return String(currentValue) === String(restriction.parameterValue)
    })
  }, [itemKey, itemProps.extension.configurationParamRestrictions, itemProps.productKey, values])

  const onToggle = (value: boolean) => {
    if (!isApplicable) {
      return
    }
    if (value) {
      itemProps.onOpenExtraParamModal(itemProps.extension)
      return
    }
    field.onChange(false)
    trackEvent('ExtensionSwitchToggled', { id: name, value: false })
    trackPriceChange('extension_toggle')
  }

  const onTogglePress = () => onToggle(!isSelected)
  const minPrice = itemProps.extension.minPremium[itemProps.premiumIntervalKey]

  return (
    <View className={styles.card()}>
      <Pressable className={styles.toggleRow()} onPress={onTogglePress}>
        <View className={styles.toggleContent()}>
          {renderExtensionIcon(itemProps.extension) ? (
            <View className={styles.iconWrapper()}>{renderExtensionIcon(itemProps.extension)}</View>
          ) : null}
          <View className={styles.textWrapper()}>
            <Text className={h4()}>{itemProps.extension.title}</Text>
            {!isSelected && (
              <Text className={body2({ color: 'grey500' })}>
                from +{formatCurrency(minPrice)}/{itemProps.premiumIntervalLabel}
              </Text>
            )}
          </View>
        </View>
        <Switch
          id={switchId}
          testID={switchId}
          value={isSelected}
          onValueChange={onToggle}
          label={itemProps.extension.title}
        />
      </Pressable>
      <Spacer size="xsmall" />
      <Text className={body2({ color: 'grey700' })}>{itemProps.extension.description}</Text>
      <Spacer size="xsmall" />
      <Pressable onPress={() => itemProps.onOpenLearnMore(itemProps.extension)}>
        <Text className={body2({ color: 'deepForest' })}>
          {t('sharedFlows_common:steps.quote.extensions.learn_more')} {'>'}
        </Text>
      </Pressable>
    </View>
  )
}

const ExtensionExtraParamModal = ({
  extension,
  extraParams,
  isBike,
  productKey,
  extraParamPath,
  extraValue,
  setValue: setFormValue,
  isConfirmDisabled,
  onConfirm,
  onCancel,
  modalKey,
}: {
  extension: ProductExtension
  extraParams: { paramName: string; label: string; min: number; max: number; infoText: string }
  isBike: boolean
  productKey: ProductKey
  extraParamPath: string | undefined
  extraValue: unknown
  setValue: (name: string, value: unknown) => void
  isConfirmDisabled: boolean
  onConfirm: () => void
  onCancel: () => void
  modalKey: string
}) => {
  const confirmStyles = extensionStyles({ confirmDisabled: isConfirmDisabled })

  return (
    <QuoteModal onClose={onCancel}>
      <>
        <View className={styles.modalHeader()}>
          <Text className={`${styles.modalTitleText()} ${h4()}`}>
            {isBike
              ? 'Please indicate the sum insured. This corresponds to the purchase price of your bicycle according to the invoice.'
              : extension.title}
          </Text>
          <Pressable
            testID={`extension_params_modal_${modalKey}_close_button`}
            onPress={onCancel}
            className={styles.modalCloseButton()}
          >
            <PhosphorIcon name="X" size={24} />
          </Pressable>
        </View>
        <Text className={body2({ color: 'grey700' })}>
          {isBike ? 'Insure your bike along with your belongings.' : extension.description}
        </Text>
        <Spacer size="medium" />
        <Text className={body2({ color: 'grey700' })}>{extraParams.label}</Text>
        <Spacer size="xsmall" />
        <TextInput
          nativeID={`products-${productKey}-configuration_parameters-${extraParams.paramName}`}
          testID={`products-${productKey}-configuration_parameters-${extraParams.paramName}`}
          value={match(extraValue)
            .with(P.string, P.number, String)
            .otherwise(() => '')}
          onChangeText={(text) => {
            if (!extraParamPath) {
              return
            }
            const num = Number.parseInt(text) || undefined
            setFormValue(extraParamPath, num)
          }}
          keyboardType="numeric"
          placeholder={extraParams.label}
          className={styles.modalInput()}
        />
        <Spacer size="small" />
        <View className={styles.modalInfoBox()}>
          <Text className={body2({ color: 'grey700' })}>{extraParams.infoText}</Text>
        </View>
        <Spacer size="medium" />
        <Pressable
          testID={`extension_params_modal_${modalKey}_confirm_button`}
          onPress={onConfirm}
          className={confirmStyles.modalConfirmButton()}
          disabled={isConfirmDisabled}
        >
          <Text
            className={body1({
              bold: true,
              color: isConfirmDisabled ? 'grey700' : 'white',
            })}
          >
            Confirm
          </Text>
        </Pressable>
      </>
    </QuoteModal>
  )
}

const ExtensionContentModal = ({
  extraContent,
  stepContentRenderer,
  componentMap,
  onConfirm,
  onCancel,
  modalKey,
}: {
  extraContent: ExtensionExtraContent
  stepContentRenderer: (props: any) => React.ReactElement
  componentMap?: Record<string, React.ElementType>
  onConfirm: () => void
  onCancel: () => void
  modalKey: string
}) => {
  const { t } = useTranslation()
  const { control, watch } = useFormContext()
  const { flowName } = useQuoteData()
  const requiredPaths = extraContent.requiredPaths ?? []
  const requiredValues = useWatch({ control, name: requiredPaths })
  const isConfirmDisabled = requiredValues.some((value) =>
    Array.isArray(value) ? value.length === 0 : value === undefined || value === ''
  )
  const confirmStyles = extensionStyles({ confirmDisabled: isConfirmDisabled })

  return (
    <QuoteModal onClose={onCancel} scrollable>
      <>
        <View className="flex-row justify-end">
          <Pressable testID={`extension_params_modal_${modalKey}_close_button`} onPress={onCancel}>
            <PhosphorIcon name="X" size={24} />
          </Pressable>
        </View>
        {stepContentRenderer({
          elements: extraContent.content,
          componentMap,
          flowValues: watch(),
          flowName,
          hasMinHeight: false,
        })}
        <Spacer size="medium" />
        <Pressable
          testID={`extension_params_modal_${modalKey}_confirm_button`}
          onPress={onConfirm}
          className={confirmStyles.modalConfirmButton()}
          disabled={isConfirmDisabled}
        >
          <Text className={body1({ bold: true, color: isConfirmDisabled ? 'grey700' : 'white' })}>
            {t('sharedFlows_common:generic.buttons.continue')}
          </Text>
        </Pressable>
      </>
    </QuoteModal>
  )
}

const ExtensionItem = (itemProps: ItemProps) => {
  if (itemProps.hasModal) {
    return <ExtensionItemWithExtraModal {...itemProps} />
  }
  return <ExtensionItemSimple {...itemProps} />
}

const ExtensionLearnMoreModal = ({
  extension,
  onClose,
}: {
  extension: ProductExtension
  onClose: () => void
}) => {
  const [expandedItem, setExpandedItem] = useState<string | null>(null)

  return (
    <QuoteModal onClose={onClose} scrollable>
      <>
        <View className="flex-row items-center justify-between">
          <Text className={h3()}>{extension.title}</Text>
          <Pressable onPress={onClose}>
            <PhosphorIcon name="X" size={24} />
          </Pressable>
        </View>
        <Spacer size="small" />
        {extension.contentSubGroups?.map((subGroup) => (
          <SubGroupAccordion
            key={subGroup.itemKey?.current ?? subGroup.title}
            subGroup={subGroup}
            expandedItem={expandedItem}
            onToggleItem={setExpandedItem}
            defaultExpanded
          />
        ))}
      </>
    </QuoteModal>
  )
}

export const Extensions = (props: Props) => {
  const { t } = useTranslation()
  const { premiumInterval, trackEvent } = useQuoteData()
  const { trackPriceChange } = usePriceChangeTracking()
  const { setValue, watch } = useFormContext()
  const [openExtraModalKey, setOpenExtraModalKey] = useState<string | null>(null)
  const [learnMoreExtension, setLearnMoreExtension] = useState<ProductExtension | null>(null)

  const premiumIntervalKey = premiumInterval === 'P1M' ? 'p1M' : 'p1Y'
  const premiumIntervalLabel = premiumInterval === 'P1M' ? 'month' : 'year'

  const activeExtension = useMemo(
    () =>
      props.extensions.find(
        (extension) => extension.configurationDataExtensionKey === openExtraModalKey
      ),
    [props.extensions, openExtraModalKey]
  )
  const extraContentFor = (extension: ProductExtension) =>
    props.extraContent?.[extension.configurationDataExtensionKey]
  const opensModal = (extension: ProductExtension) =>
    Boolean(extraContentFor(extension)) || Boolean(getExtraParamsForExtension(extension))

  const activeItemKey = activeExtension?.configurationDataExtensionKey
  const activeExtraContent = activeItemKey ? props.extraContent?.[activeItemKey] : undefined
  const activeExtraParams =
    activeExtension && !activeExtraContent ? getExtraParamsForExtension(activeExtension) : undefined
  const activeTogglePath = activeItemKey && getProductParamPath(props.productKey, activeItemKey)
  const activeExtraParamPath =
    activeExtraParams && getProductParamPath(props.productKey, activeExtraParams.paramName)
  const activeExtraValue = activeExtraParamPath ? watch(activeExtraParamPath) : undefined
  const isBikeExtraModal = activeExtraParams?.paramName === 'bike_theft_insured_sum_amount'

  const openModalForExtension = (extension: ProductExtension) => {
    if (opensModal(extension)) {
      setOpenExtraModalKey(extension.configurationDataExtensionKey)
    }
  }

  const onConfirmExtraParams = () => {
    if (!activeTogglePath) {
      return
    }
    setValue(activeTogglePath, true)
    setOpenExtraModalKey(null)
    trackEvent('ExtensionSwitchToggled', { id: activeTogglePath, value: true })
    trackPriceChange('extension_toggle')
  }

  const onCancelExtraParams = () => {
    if (activeTogglePath) {
      setValue(activeTogglePath, false)
    }
    if (activeExtraParamPath) {
      setValue(activeExtraParamPath, undefined)
    }
    for (const path of activeExtraContent?.requiredPaths ?? []) {
      setValue(path, undefined)
    }
    setOpenExtraModalKey(null)
  }

  const isExtraParamConfirmDisabled =
    !activeExtraParams ||
    !activeExtraValue ||
    activeExtraValue < activeExtraParams.min ||
    activeExtraValue > activeExtraParams.max

  if (props.extensions.length === 0) {
    return null
  }

  return (
    <View className={styles.container()}>
      <Text className={h3()}>{t('sharedFlows_common:steps.quote.extensions.title')}</Text>
      {props.extensions.map((extension) => (
        <ExtensionItem
          key={extension.configurationDataExtensionKey}
          extension={extension}
          productKey={props.productKey}
          premiumIntervalKey={premiumIntervalKey}
          premiumIntervalLabel={premiumIntervalLabel}
          hasModal={opensModal(extension)}
          onOpenExtraParamModal={openModalForExtension}
          onOpenLearnMore={(ext) => {
            setLearnMoreExtension(ext)
            trackEvent(EVENTS.PopupOpened, {
              id: `extension_learn_more_${ext.configurationDataExtensionKey}`,
            })
          }}
        />
      ))}

      {learnMoreExtension && (
        <ExtensionLearnMoreModal
          extension={learnMoreExtension}
          onClose={() => setLearnMoreExtension(null)}
        />
      )}

      {activeExtraContent && openExtraModalKey && (
        <ExtensionContentModal
          extraContent={activeExtraContent}
          stepContentRenderer={props.stepContentRenderer}
          componentMap={props.componentMap}
          onConfirm={onConfirmExtraParams}
          onCancel={onCancelExtraParams}
          modalKey={openExtraModalKey}
        />
      )}

      {activeExtension && activeExtraParams && openExtraModalKey && (
        <ExtensionExtraParamModal
          extension={activeExtension}
          extraParams={activeExtraParams}
          isBike={isBikeExtraModal}
          productKey={props.productKey}
          extraParamPath={activeExtraParamPath}
          extraValue={activeExtraValue}
          setValue={setValue}
          isConfirmDisabled={isExtraParamConfirmDisabled}
          onConfirm={onConfirmExtraParams}
          onCancel={onCancelExtraParams}
          modalKey={openExtraModalKey}
        />
      )}
    </View>
  )
}
